import React, {type ReactNode} from 'react'
import classNames from '@/Utils/ClassNames'

interface BadgeProps {
    children: string | ReactNode
    theme?: 'royal' | 'shamrock' | 'error' | 'gold' | 'neutral' | 'green'
    icon?: boolean
    tightness?: 'tight' | 'standard'
    'aria-label'?: string
    testId?: string
}

export default function Badge({
                                  children,
                                  theme,
                                  tightness = 'standard',
                                  icon,
                                  'aria-label': ariaLabel,
                                  testId,
                              }: BadgeProps): ReactNode {
    const themeClasses = (): string[] => {
        const classes = ['inline-flex items-center text-xs px-2 py-1 rounded-full text-nowrap ring-1 ring-gray-500/5 ring-inset']

        switch (theme) {
            case 'error':
                classes.push('bg-crimson-90 text-crimson-30 dark:text-crimson-70 dark:bg-crimson-20')
                break

            case 'shamrock':
                classes.push('bg-shamrock-50 text-shamrock-10 dark:bg-storm-20 dark:text-shamrock-30')
                break

            case 'green':
                classes.push('bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400')
                break

            case 'gold':
                classes.push('bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400')
                break

            case 'neutral':
                classes.push('bg-gray-100 text-gray-600 dark:bg-storm-20 dark:text-storm-60')
                break

            case 'royal':
            default:
                classes.push('bg-royal-50 text-storm-10 dark:bg-storm-20 dark:text-royal-30')
                break
        }

        switch (tightness) {
            case 'tight':
                classes.push('text-[10px]')
                break

            case 'standard':
            default:
                break
        }

        classes.push(icon ? 'px-1.5 py-1' : 'font-bold text-[12px]')

        switch (tightness) {
            case 'tight':
                classes.push('px-2')
                break

            case 'standard':
            default:
                classes.push('px-3 py-1')
                break
        }

        return classes
    }

    return (
        <span
            aria-label={ariaLabel}
            data-testid={testId}
            className={classNames(
                ...themeClasses(),
                `rounded-2xl`,
            )}>
      {children}
    </span>
    )
}
